import { useState, useEffect } from "react";
import { Card } from "../ui/card";
import { X } from "lucide-react";

// Simplified notification component - not connected to context yet
const NotificationToast = () => {
  // For demonstration purposes, show a welcome notification
  const [notification, setNotification] = useState<{
    visible: boolean;
    title: string;
    message: string;
    type: "success" | "error" | "info" | "warning";
  } | null>(null);

  useEffect(() => {
    // Show a welcome notification after a short delay
    const timer = setTimeout(() => {
      setNotification({
        visible: true,
        title: "Welcome to SurpriseTable",
        message: "Discover extraordinary dining experiences.",
        type: "success"
      });
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Auto-hide notification after 5 seconds
    if (notification?.visible) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [notification]);

  const getIconByType = (type: string) => {
    switch (type) {
      case "success":
        return (
          <div className="rounded-full bg-green-100 p-2 dark:bg-green-900">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 text-green-600 dark:text-green-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        );
      case "error":
        return (
          <div className="rounded-full bg-red-100 p-2 dark:bg-red-900">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 text-red-600 dark:text-red-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        );
      case "warning":
        return (
          <div className="rounded-full bg-yellow-100 p-2 dark:bg-yellow-900">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 text-yellow-600 dark:text-yellow-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        );
      default:
        return (
          <div className="rounded-full bg-blue-100 p-2 dark:bg-blue-900">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 text-blue-600 dark:text-blue-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        );
    }
  };

  if (!notification?.visible) return null;

  return (
    <div className="fixed right-4 top-20 z-50 w-80 transform transition-transform duration-300">
      <Card className="flex items-start bg-white p-4 dark:bg-gray-800">
        <div className="mr-3">{getIconByType(notification.type)}</div>
        <div className="flex-1">
          <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200">
            {notification.title}
          </h4>
          <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
            {notification.message}
          </p>
        </div>
        <button
          onClick={() => setNotification(null)}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <X className="h-4 w-4" />
        </button>
      </Card>
    </div>
  );
};

export default NotificationToast;
